import React from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";

import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { useUser } from "@hooks/useUser";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useUpdateUserMutation } from "@api/user.api";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";

const updateNameFormSchema = z.object({
    preferredName: z.string().min(1, "Preferred name is required"),
});

type UpdateNameFormValues = z.infer<typeof updateNameFormSchema>;

const fields = createFormikFields<UpdateNameFormValues>();

interface Props {
    show: boolean;
    onClose(): void;
}

export const UpdateNameModal: React.FC<Props> = ({ show, onClose }) => {
    const user = useUser();
    const updateUserMutation = useUpdateUserMutation();
    const { error } = useToaster();

    const formik = useFormik<UpdateNameFormValues>({
        initialValues: {
            preferredName: user.preferredName ?? "",
        },
        validate: validateFormikWithZodSchema(updateNameFormSchema),
        onSubmit: submitHelper({
            handler: async ({ preferredName }) => {
                await updateUserMutation.mutateAsync({ preferredName });

                onClose();
            },
            failureHandler: async () => {
                error("Failed to update profile");
            },
        }),
    });

    return (
        <PlainModal show={show} onHide={onClose}>
            <FormikProvider value={formik}>
                <Form>
                    <StandardModalHeader>Update full name</StandardModalHeader>
                    <VStack gap={16}>
                        <fields.Text
                            name="preferredName"
                            label="Full name"
                            showErrorMessage={false}
                            size="xl"
                        />
                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={onClose}
                                disabled={formik.isSubmitting}
                            >
                                Cancel
                            </Button>
                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                disabled={!formik.isValid}
                                loading={!!formik.isSubmitting}
                                type="submit"
                                hotKey="mod+enter"
                                hotKeyOptions={{
                                    description: "Save full name",
                                    preventDefault: true,
                                    enableOnFormTags: true,
                                    scopes: [HOTKEYS_SCOPES.SETTINGS_MODAL],
                                }}
                            >
                                Save
                            </ButtonWithLoader>
                        </HStack>
                    </VStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
